/* @ngInject */

export default (
  $q,
  helperService,
  Household,
  HouseholdExpenseType,
  HouseholdDescription,
  HouseholdCategory
) => {
  /**
   * Get available expenses descriptions.
   *
   * @returns {Array}
   */
  const getAvailableExpenseDescriptions = () => HouseholdDescription.available();

  /**
   * Add household.
   *
   * @param {Array} expenses
   *
   * @returns {Array}
   */
  const addHousehold = ({ expenses }) => expenses.push(Household.blank());

  /**
   * Remove household.
   *
   * @param {Array} collection
   * @param {Object} item
   * @param {String} tabName
   *
   * @returns {Boolean}
   */
  const removeHousehold = ({ collection, item, tabName }) => {
    const deferred = $q.defer();
    if (collection?.length && item && !_.isEmpty(item)) {
      helperService.deleteItem(collection, item, `${tabName}`).then((result) => {
        deferred.resolve(result);
      });
    }
    return deferred.promise;
  };

  /**
   * Get household contacts.
   *
   * @param {Array} contacts
   * @param {Array} idContacts
   *
   * @returns {Object}
   */
  const getHouseholdContacts = ({ contacts, idContacts }) => {
    const householdContacts = [];
    const len = contacts?.length || 0;
    for (let c = 0; c < len; c += 1) {
      const contact = contacts[c];
      if (idContacts.indexOf(contact?.id) > -1) {
        householdContacts.push(contact);
      }
    }
    return householdContacts;
  };

  /**
   * Get state by household.
   *
   * @param {Array} householdsHem
   * @param {Object} household
   *
   * @returns {String}
   */
  const getStateByHousehold = ({ householdsHem, household }) => {
    const householdData = _.filter(householdsHem || [], (h) => h?.key === household?.id);
    if (householdData?.length === 1) {
      return householdData[0]?.state || '';
    }
    if (householdData?.length > 1) {
      const higherIncome = _.max(_.map(householdData || [], 'totalIncome'));
      if (higherIncome) {
        const currentContact = _.find(
          householdData || [],
          (h) => h?.totalIncome === higherIncome
        );
        if (currentContact) {
          return currentContact?.state || '';
        }
      }
    }
    return '';
  };

  /**
   * Get total or group total expenses by household.
   *
   * @param {Array} householdExpenses
   *
   * @returns {Number}
   */
  const getTotalOrGroupTotalExpensesByHousehold = ({ householdExpenses }) =>
    Household.getTotalGroupOrAllExpensesByHousehold(householdExpenses);

  /**
   * Get total household living expenses.
   *
   * @param {Array} householdExpenses
   *
   * @returns {Number}
   */
  const getTotalLivingExpensesByHousehold = ({ householdExpenses }) => {
    const livingExpenses = _.flatten(HouseholdCategory.hem.types);
    const expenses = _.filter(
      householdExpenses,
      (h) => livingExpenses.indexOf(h?.type?.name) > -1
    );
    return helperService.round({
      value: _.reduce(
        expenses,
        (sum, n) =>
          _.add(sum, _.multiply(n?.value, helperService.getFrequencyValue(n?.frequency, true))),
        0
      )
    });
  };

  /**
   * Get total household other expenses.
   *
   * @param {Array} householdExpenses
   * @param {Boolean} useRent
   * @param {Boolean} isExpensesSection
   *
   * @returns {Number}
   */
  const getTotalOtherExpensesByHousehold = ({
    householdExpenses,
    useRent,
    isExpensesSection
  }) => {
    let otherExpenses = [];
    if (isExpensesSection) {
      otherExpenses = _.without(
        HouseholdCategory.other.types,
        HouseholdExpenseType.child_or_spousal_maintenance.name,
        HouseholdExpenseType.current_rent_expense.name,
        HouseholdExpenseType.ongoing_board_expense.name
      );
    } else {
      otherExpenses = !useRent
        ? HouseholdCategory.other.types
        : _.without(
            HouseholdCategory.other.types,
            HouseholdExpenseType.current_rent_expense.name,
            HouseholdExpenseType.ongoing_board_expense.name
          );
    }
    const expenses = _.filter(
      householdExpenses,
      (h) => otherExpenses.indexOf(h?.type?.name) > -1
    );
    return helperService.round({
      value: _.reduce(
        expenses,
        (sum, n) =>
          _.add(sum, _.multiply(n?.value, helperService.getFrequencyValue(n?.frequency, true))),
        0
      )
    });
  };

  /**
   * Get total expenses.
   *
   * @param {Array} expenses
   * @param {Array} contacts
   *
   * @returns {Number}
   */
  const getTotalExpenses = ({ expenses, contacts }) => {
    let total = 0;
    const householdExpenses = _.filter(expenses, (e) => e?.id && _.isUuid(e?.id));
    const len = householdExpenses.length;
    for (let h = 0; h < len; h += 1) {
      const household = householdExpenses[h];
      const householdContacts = getHouseholdContacts({
        contacts,
        idContacts: household?.idContacts || []
      });
      if (householdContacts?.length) {
        total += getTotalOrGroupTotalExpensesByHousehold({
          householdExpenses: household?.householdExpenses || []
        });
      }
    }
    return total;
  };

  return {
    getAvailableExpenseDescriptions,
    addHousehold,
    removeHousehold,
    getHouseholdContacts,
    getStateByHousehold,
    getTotalOrGroupTotalExpensesByHousehold,
    getTotalLivingExpensesByHousehold,
    getTotalOtherExpensesByHousehold,
    getTotalExpenses
  };
};
